export type ExperimentName = `experiment.${string}`
type Experiment = {
  defaultValue: 'noVariation'
  urlPatterns: Array<string>
}

/**
 * This dictionary holds the configuration for all the experiments currently
 * being assigned by the experiments worker.
 *
 * @property defaultValue - Must be set to noVariation for all of them.
 * @property urlPatterns - Allows you to scope each experiment to their
 * associated set of pages. Those patterns are used on the worker side
 * to know if the experiment will be used to process the current request
 * or not. It helps us improving our cache hit ratio, and thus the
 * overall server-side performance.
 */
export const config = {
  'experiment.globalAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.bm4bHeaderButtonV2': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBmProHeaderButton
     * withoutBmProHeaderButton
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.homeAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/?$' /* Home page */],
  },
  'experiment.landingAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* Landing page */],
  },
  'experiment.productAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.checkoutAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.homeCMSPageName': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/?$' /* Home page - permanent AB test */],
  },
  'experiment.goodDealsEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - GoodDeal */,
    ],
  },
  'experiment.recyclingEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - Recycling */,
    ],
  },
  'experiment.holidaysEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - Holidays */,
    ],
  },
  'experiment.homeBuybackPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages - permanent AB test */,
    ],
  },
  // https://backmarket.atlassian.net/wiki/spaces/2C/pages/4526833841/Filtering+cart+insurances+monthly+only+vs+upfront+only
  'experiment.monthlyFilterExperiment': {
    defaultValue: 'noVariation',
    /** Variations:
     * noFilter
     * monthlyOnly
     * upfrontOnly
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/SR/pages/5334073749/PRD+Gift+Cards+POC?focusedCommentId=5750030399
  'experiment.giftCards': {
    defaultValue: 'noVariation',
    /** Variations:
     * withGiftCards
     * withoutGiftCards */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/2C/pages/5173084653/PRD+BM-branded+mobile+plans+fake+door+test+1
  'experiment.mobilePlanFakeDoor': {
    /** Variations:
     * withoutMobilePlan
     * withBackMarketMobilePlan
     * withExternalTelcoMobilePlan */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.enableScalapayIT': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages (in the footer) */],
  },
  'experiment.reviewSellerAndFilters': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * showSeller */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/p/.*' /* Product Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/s/.*' /* Seller Reviews pages */,
    ],
  },
  'experiment.reviewAmount': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * tenReviews
     * sevenReviews */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
    ],
  },
  'experiment.reviewsAiSummary': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutAIReview
     * withAIReview
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.reviewsNewAITags': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutAITags
     * withAITags
     * withHighlightedAITags
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.reviewsHelpRequestStatus': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutHelpRequestStatus
     * withHelpRequestStatus
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* All reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/s/.*' /* Seller pages */,
    ],
  },
  'experiment.sellerRatingPDP': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
    /** Variations:
     * noVariation
     * withSellerRating
     * withoutSellerRating
     */
  },
  'experiment.expressCheckout': {
    /** Variations:
     * enabled
     * disabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.checkoutCollectionPointOption': {
    /** Variations:
     * collectionPointEnabled
     * collectionPointDisabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.mwebCartSummary': {
    /** Variations:
     * originalMwebCartSummary
     * topMwebCartSummary
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.priceChangeNotification': {
    /** Variations:
     * priceChangeHidden
     * priceChangeHighlighted
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.serviceFeeGrid': {
    /** Variations:
     * newServiceFeeGrid
     * currentServiceFeeGrid
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },

  'experiment.countrySwitchRedirection': {
    /** Variations:
     * withRedirection
     * withoutRedirection */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.deliveryDateAdjustment': {
    /** Variations:
     * withAdjustment
     * withoutAdjustment */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.flashSales': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.newOfferPageV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * withGraph
     * withoutGraph */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.resaleBankStep': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBankStep
     * withoutBankStep */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.ppNewATCButtonDesign': {
    defaultValue: 'noVariation',
    /** Variations:
     * withoutNewATCButtonDesign
     * withNewATCButtonDesign */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppNewPriceDesign': {
    defaultValue: 'noVariation',
    /** Variations:
     * withoutNewPriceDesign
     * withNewPriceDesign */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppReviewsInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withoutReviewsInCarousel
     * withReviewsInCarousel */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
    ],
  },
  'experiment.promoCodeInCart': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.hidePlpBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.ppJapanBlackFridaySpecial': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBlackFridaySpecial
     * withoutBlackFridaySpecial */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.ppJapanBatteryPickerUpdated': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBatteryPickerUpdated
     * withoutBatteryPickerUpdated */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.ppJapanPremiumDefinitionUpdated': {
    defaultValue: 'noVariation',
    /** Variations:
     * withPremiumDefinitionUpdated
     * withoutPremiumDefinitionUpdated */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.tradeInAU': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/en-au/p/.*' /* Product page */],
  },
  'experiment.ppUpgradeForLess': {
    defaultValue: 'noVariation',
    /** Variations:
     * withUpgrade
     * withoutUpgrade */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.cmsPromotionalBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * controlText
     * variantText
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages (above Header) */],
  },
  'experiment.skinnyOrPromotionalBannerV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * skinnyBanner
     * promotionalBanner
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}' /* Home page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */,
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/search' /* SearchResultPage */,
      '^/[a-z]{2}-[a-z]{2}/s/.*' /* SellerPage */,
    ],
  },
  'experiment.ppTelcoCalculator': {
    defaultValue: 'noVariation',
    /** Variations:
     * withCalculator
     * withouCalculator */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppAutomatedReviewsInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAutomatedReviews
     * withoutAutomatedReviews */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.aiShoppingAssistant': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.enableEsUsContent': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.enableEsUsLocaleSelector': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.inputRedesign': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.ppOrderBefore': {
    defaultValue: 'noVariation',
    /** Variations:
     * withOrderBefore
     * withoutOrderBefore */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppAppleWatchBand': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBand
     * withoutBand */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */,
    ],
  },
  'experiment.ppUnitSold': {
    defaultValue: 'noVariation',
    /** Variations:
     * withUnitSold
     * withoutUnitSold */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppOptimiseGrades': {
    defaultValue: 'noVariation',
    /** Variations:
     * withOptimisedGrades
     * withoutOptimisedGrades */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.plpDuplicatedAlgoliaCalls': {
    defaultValue: 'noVariation',
    /** Variations:
     * withDuplicatedCalls
     * withoutDuplicatedCalls */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */],
  },
  'experiment.buybackIcon': {
    defaultValue: 'noVariation',
    /** Variations:
     * withCircleIcon */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.ppStickerFlagV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * withFlag
     * withoutFlag */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.precisionGrading': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.ppPriceOpportunity': {
    defaultValue: 'noVariation',
    /** Variations:
     * withPriceOpportunity
     * withoutPriceOpportunity */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.deliveryPromiseBgWeb': {
    defaultValue: 'noVariation',
    /** Variations:
     * deliveryPromiseNoBg
     * deliveryPromiseWithBgAbove
     * deliveryPromiseWithBgBelow
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
} satisfies Record<ExperimentName, Experiment>
